import React, { useEffect, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Text,
  ButtonNext,
  ButtonBack,
  Buttons,
  StyledImage,
  SetPrintArea,
  SetHorizontalLine,
  SetVerticalLine,
  ResizeLogo,
  ImageContainer,
  MoveContainer,
  RowPrintArea,
  Background,
  PhotosContainer,
  Photo,
  PrintAreaColumn,
  Button,
} from "../Steps.styles";

import Draggable from "react-draggable";
import { ResizableBox } from "react-resizable";
import {
  addItemDisplayPhoto,
  addItemPhotos,
  addItemPrintAreas,
  setPrintAreasToStore,
} from "../../../store/slices/marketplace/marketplaceSlice";
import { ReactComponent as ResizeIcon } from "../../../assets/icons/resizeLogo.svg";
import { formatFileID } from "../../../utils/utils";

const AddItemStepPrintArea = ({ onNext, onBack, photos }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [printAreas, setPrintAreas] = useState({});
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const addItemPhotosArr = useSelector(addItemPhotos);
  const photoIndex = useSelector(addItemDisplayPhoto);
  const printAreasStore = useSelector(addItemPrintAreas);

  const itemPhotoRef = useRef(null);
  const [scale, setScale] = useState({ x: 1, y: 1 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [initializedFromStore, setInitializedFromStore] = useState(false);

  const computeScale = useCallback(() => {
    const el = itemPhotoRef.current;
    if (!el || !el.naturalWidth || !el.width) return { x: 1, y: 1 };
    return {
      x: el.naturalWidth / el.width,
      y: el.naturalHeight / el.height,
    };
  }, []);

  const deriveUIFromStore = useCallback(
    (photoId, currentScale = scale) => {
      if (!printAreasStore || !printAreasStore[photoId]) return null;
      const stored = printAreasStore[photoId];

      const hasScaledValues =
        typeof stored.topScaled !== "undefined" &&
        typeof stored.leftScaled !== "undefined" &&
        typeof stored.widthScaled !== "undefined" &&
        typeof stored.heightScaled !== "undefined";

      if (hasScaledValues) {
        return {
          top: stored.topScaled / (currentScale.y || 1),
          left: stored.leftScaled / (currentScale.x || 1),
          width: stored.widthScaled / (currentScale.x || 1),
          height: stored.heightScaled / (currentScale.y || 1),
          topScaled: stored.topScaled,
          leftScaled: stored.leftScaled,
          widthScaled: stored.widthScaled,
          heightScaled: stored.heightScaled,
        };
      } else {
        return {
          top: (stored.top || 0) / (currentScale.y || 1),
          left: (stored.left || 0) / (currentScale.x || 1),
          width: (stored.width || 0) / (currentScale.x || 1),
          height: (stored.height || 0) / (currentScale.y || 1),
          topScaled: stored.top || 0,
          leftScaled: stored.left || 0,
          widthScaled: stored.width || 0,
          heightScaled: stored.height || 0,
        };
      }
    },
    [printAreasStore, scale]
  );

  const buildStoreFromLocal = useCallback(
    (local = printAreas) => {
      const out = {};
      for (const [key, val] of Object.entries(local || {})) {
        out[key] = {
          topScaled:
            typeof val.topScaled !== "undefined"
              ? Math.round(val.topScaled)
              : Math.round((val.top || 0) * (scale.y || 1)),
          leftScaled:
            typeof val.leftScaled !== "undefined"
              ? Math.round(val.leftScaled)
              : Math.round((val.left || 0) * (scale.x || 1)),
          widthScaled:
            typeof val.widthScaled !== "undefined"
              ? Math.round(val.widthScaled)
              : Math.round((val.width || 0) * (scale.x || 1)),
          heightScaled:
            typeof val.heightScaled !== "undefined"
              ? Math.round(val.heightScaled)
              : Math.round((val.height || 0) * (scale.y || 1)),
        };
      }
      return out;
    },
    [printAreas, scale]
  );

  useEffect(() => {
    if (photoIndex === null || photoIndex === undefined) return;

    let imageURL = null;
    let photoId = null;

    if (photoIndex.type === "url" || photoIndex.type === "ai") {
      const photo = addItemPhotosArr.find((p) => p.url === photoIndex.id);
      imageURL = photo?.url ?? null;
      photoId = photo?.url ?? null;
    } else if (photoIndex.type === "file") {
      const file = photos.find((p) => formatFileID(p) === photoIndex.id);
      if (file) {
        imageURL = URL.createObjectURL(file);
        photoId = formatFileID(file);
      }
    }

    setImageSrc(imageURL);
    setSelectedPhotoIndex(photoId);
    setImageLoaded(false);
  }, [addItemPhotosArr, photoIndex, photos]);

  useEffect(() => {
    if (
      initializedFromStore ||
      !printAreasStore ||
      Object.keys(printAreasStore).length === 0
    )
      return;
    setInitializedFromStore(true);
  }, [printAreasStore, initializedFromStore]);

  const handleImageLoad = useCallback(() => {
    if (!itemPhotoRef.current) return;

    requestAnimationFrame(() => {
      const el = itemPhotoRef.current;
      if (!el) return;

      const sc = {
        x: el.naturalWidth / el.width,
        y: el.naturalHeight / el.height,
      };

      setScale(sc);
      setImageLoaded(true);

      if (!selectedPhotoIndex) return;

      if (!printAreas[selectedPhotoIndex]) {
        const ui = deriveUIFromStore(selectedPhotoIndex, sc);
        if (ui) {
          setPrintAreas((prev) => ({
            ...prev,
            [selectedPhotoIndex]: ui,
          }));
        }
      }
    });
  }, [deriveUIFromStore, selectedPhotoIndex, printAreas]);

  const handleAddPrintArea = (photoId) => {
    if (!photoId || !itemPhotoRef.current) return;
    const sc = computeScale();
    const defaultsUI = {
      top: 150,
      left: 160,
      width: 180,
      height: 200,
    };
    const nat = {
      topScaled: Math.round(defaultsUI.top * sc.y),
      leftScaled: Math.round(defaultsUI.left * sc.x),
      widthScaled: Math.round(defaultsUI.width * sc.x),
      heightScaled: Math.round(defaultsUI.height * sc.y),
    };

    setScale(sc);
    setPrintAreas((prev) => ({
      ...prev,
      [photoId]: {
        ...defaultsUI,
        ...nat,
      },
    }));

    const updated = {
      ...printAreasStore,
      [photoId]: nat,
    };
    dispatch(setPrintAreasToStore(updated));
  };

  const handleRemovePrintArea = (photoId) => {
    setPrintAreas((prev) => {
      const { [photoId]: _, ...rest } = prev;
      return rest;
    });

    const updated = { ...printAreasStore };
    delete updated[photoId];
    dispatch(setPrintAreasToStore(updated));
  };

  const handleDrag = (e, data, photoId) => {
    if (!photoId) return;
    const sc = scale;
    setPrintAreas((prev) => {
      const prevArea = prev[photoId] || {};
      const newArea = {
        ...prevArea,
        top: data.y,
        left: data.x,
        topScaled: Math.round(data.y * sc.y),
        leftScaled: Math.round(data.x * sc.x),
      };
      return { ...prev, [photoId]: newArea };
    });

    const updated = {
      ...printAreasStore,
      [photoId]: {
        topScaled: Math.round(data.y * sc.y),
        leftScaled: Math.round(data.x * sc.x),
        widthScaled:
          printAreasStore[photoId]?.widthScaled || Math.round(180 * sc.x),
        heightScaled:
          printAreasStore[photoId]?.heightScaled || Math.round(200 * sc.y),
      },
    };
    dispatch(setPrintAreasToStore(updated));
  };

  const handleResize = (e, data, photoId) => {
    if (!photoId) return;
    const sc = scale;
    setPrintAreas((prev) => {
      const prevArea = prev[photoId] || {};
      const newArea = {
        ...prevArea,
        width: data.size.width,
        height: data.size.height,
        widthScaled: Math.round(data.size.width * sc.x),
        heightScaled: Math.round(data.size.height * sc.y),
      };
      return { ...prev, [photoId]: newArea };
    });

    const updated = {
      ...printAreasStore,
      [photoId]: {
        topScaled:
          printAreasStore[photoId]?.topScaled || Math.round(150 * sc.y),
        leftScaled:
          printAreasStore[photoId]?.leftScaled || Math.round(160 * sc.x),
        widthScaled: Math.round(data.size.width * sc.x),
        heightScaled: Math.round(data.size.height * sc.y),
      },
    };
    dispatch(setPrintAreasToStore(updated));
  };

  const currentArea = selectedPhotoIndex
    ? printAreas[selectedPhotoIndex]
    : null;

  const handleSelectPhoto = (photo, type = "url") => {
    let photoId = type === "file" ? formatFileID(photo) : photo.url;
    let imageURL = type === "file" ? URL.createObjectURL(photo) : photo.url;

    setSelectedPhotoIndex(photoId);
    setImageSrc(imageURL);
    setImageLoaded(false);
  };

  const handleNext = () => {
    onNext();
  };

  return (
    <>
      <RowPrintArea $margin>
        <Text>{t("set-print-area")}:</Text>
        <ImageContainer>
          {selectedPhotoIndex && currentArea && (
            <Draggable
              position={{
                x: currentArea?.left || 0,
                y: currentArea?.top || 0,
              }}
              onDrag={(e, data) => handleDrag(e, data, selectedPhotoIndex)}
              bounds="parent"
            >
              <MoveContainer>
                <ResizableBox
                  width={currentArea?.width || 180}
                  height={currentArea?.height || 200}
                  onResize={(e, data) =>
                    handleResize(e, data, selectedPhotoIndex)
                  }
                  onResizeStart={(e) => e.stopPropagation()}
                  handle={
                    <ResizeLogo $hover>
                      <ResizeIcon />
                    </ResizeLogo>
                  }
                >
                  <SetPrintArea>
                    <Background />
                    <SetHorizontalLine />
                    <SetVerticalLine />
                  </SetPrintArea>
                </ResizableBox>
              </MoveContainer>
            </Draggable>
          )}

          <StyledImage
            src={imageSrc}
            alt="ItemPhoto"
            ref={itemPhotoRef}
            onLoad={handleImageLoad}
          />
        </ImageContainer>

        <PrintAreaColumn>
          <PhotosContainer>
            {addItemPhotosArr?.map((photo, index) => {
              const imageURL = photo.url;
              return (
                <Photo
                  key={imageURL || index}
                  src={imageURL}
                  onClick={() => handleSelectPhoto(photo, "url")}
                />
              );
            })}
            {photos?.map((photo, index) => {
              const imageURL = URL.createObjectURL(photo);
              const id = formatFileID(photo);
              return (
                <Photo
                  key={id}
                  src={imageURL}
                  onClick={() => handleSelectPhoto(photo, "file")}
                />
              );
            })}
          </PhotosContainer>

          <Button
            onClick={() => handleAddPrintArea(selectedPhotoIndex)}
            style={{ alignSelf: "center" }}
            disabled={!selectedPhotoIndex || !!printAreas[selectedPhotoIndex]}
          >
            {t("add-print-area")}
          </Button>

          {selectedPhotoIndex !== null && printAreas[selectedPhotoIndex] && (
            <Button
              onClick={() => handleRemovePrintArea(selectedPhotoIndex)}
              style={{
                alignSelf: "center",
                marginTop: "-30px",
              }}
            >
              {t("remove-print-area")}
            </Button>
          )}
        </PrintAreaColumn>
      </RowPrintArea>

      <Buttons>
        <ButtonBack
          onClick={() => {
            if (!imageLoaded) return;
            onBack();
          }}
        >
          {t("back")}
        </ButtonBack>

        <ButtonNext onClick={handleNext}>{t("next")}</ButtonNext>
      </Buttons>
    </>
  );
};

export default AddItemStepPrintArea;
